import { useState, type DragEvent, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import clsx from 'clsx'

export interface ChipInputProps {
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
  /** Chips (by exact value) to render in a "matched" success state - used by
   * PRD 03's Test panel to highlight phrases the identification engine
   * actually matched, without needing a separate read-only display. */
  highlighted?: string[]
  /** Enables drag-to-reorder within this chip list (PRD 03 Interactions -
   * cosmetic only, doesn't affect matching). Off by default so existing/
   * future plain consumers (PRD 04/05/06) are unaffected. */
  reorderable?: boolean
  /** Renders the container border in the danger colour (e.g. an empty
   * required-keyword group that blocks Save) - never colour-alone, callers
   * still pair this with an inline error message per the design system. */
  invalid?: boolean
}

// Type + enter to add a chip, click x to remove - the identical pattern used
// for keyword/alias/prompt-hint lists across PRDs 03/04/05/06.
export function ChipInput({
  value,
  onChange,
  placeholder,
  disabled,
  highlighted,
  reorderable,
  invalid,
}: ChipInputProps) {
  const [draft, setDraft] = useState('')
  const [dragIndex, setDragIndex] = useState<number | null>(null)

  function addChip() {
    const chip = draft.trim()
    if (!chip) return
    onChange([...value, chip])
    setDraft('')
  }

  function removeChip(index: number) {
    onChange(value.filter((_, i) => i !== index))
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      addChip()
    }
  }

  function handleDragStart(index: number) {
    return (event: DragEvent<HTMLSpanElement>) => {
      setDragIndex(index)
      event.dataTransfer.effectAllowed = 'move'
    }
  }

  function handleDragOver(index: number) {
    return (event: DragEvent<HTMLSpanElement>) => {
      event.preventDefault()
      if (dragIndex === null || dragIndex === index) return
      const reordered = [...value]
      const [moved] = reordered.splice(dragIndex, 1)
      reordered.splice(index, 0, moved)
      setDragIndex(index)
      onChange(reordered)
    }
  }

  function handleDragEnd() {
    setDragIndex(null)
  }

  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-2 rounded-control border bg-bg p-2',
        invalid ? 'border-danger' : 'border-border',
        disabled && 'opacity-50',
      )}
    >
      {value.map((chip, index) => {
        const isMatched = highlighted?.includes(chip)
        return (
          <span
            key={`${chip}-${index}`}
            draggable={reorderable && !disabled}
            onDragStart={reorderable ? handleDragStart(index) : undefined}
            onDragOver={reorderable ? handleDragOver(index) : undefined}
            onDragEnd={reorderable ? handleDragEnd : undefined}
            className={clsx(
              'flex items-center gap-1 rounded-pill px-2 py-1 text-meta',
              isMatched
                ? 'bg-success/10 text-success ring-1 ring-success'
                : 'bg-surface-raised text-text-primary',
              reorderable && !disabled && 'cursor-grab active:cursor-grabbing',
            )}
          >
            {chip}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${chip}`}
                onClick={() => removeChip(index)}
                className="text-text-secondary hover:text-text-primary"
              >
                <X size={12} strokeWidth={1.5} />
              </button>
            )}
          </span>
        )
      })}
      <input
        type="text"
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        className="min-w-[80px] flex-1 bg-transparent text-body text-text-primary outline-none placeholder:text-text-disabled"
      />
    </div>
  )
}
